import PropTypes from "prop-types";
import {
  Box,
  Card,
  Typography,
  Avatar,
  Stack,
  Tooltip,
  CardContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DeleteIcon from "@mui/icons-material/Delete";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import EmptyContent from "../Coupons/EmptyContent";

const PremiumCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 20px 40px rgba(0,0,0,0.04)",
  overflow: "hidden",
  position: "relative",
  background: "#ffffff",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
  },
}));

const LinkCard = styled(Card)(({ theme }) => ({
  borderRadius: "10px",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0.1rem 0.2rem 0.2rem rgba(80, 40, 10, 0.2)",
  overflow: "hidden",
  position: "relative",
  background: "#ffffff",
  transition: "transform 0.3s ease-in-out",
  margin: "0.6rem 0",
  marginTop: "15px",
  "&:hover": {
    transform: "translateY(-5px)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "5px",
    backgroundColor: "#ff715b",
    borderTopRightRadius: "10px",
    borderBottomRightRadius: "10px",
  },
}));

AppLinksByProfile.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  list: PropTypes.array.isRequired,
};

export default function AppLinksByProfile({
  type,
  list,
  onDelete,
  onEdit,
  ProfileCardVisible,
}) {
  const handleEdit = (link) => {
    if (ProfileCardVisible) ProfileCardVisible(false);
    onEdit(link);
  };

  const handleLinkClick = (link) => {
    let url = link.LinkUrl;
    if (type === "contactLinks") {
      switch (link.Category) {
        case "Phone":
        case "PhoneNumber":
          url = `tel:${url}`;
          break;
        case "Email":
        case "Gmail":
          url = `mailto:${url}`;
          break;
        case "WhatsApp":
          url = `https://wa.me/${url.replace(/\D/g, "")}`;
          break;
        default:
          break;
      }
    }
    window.open(url, "_blank");
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 360, mx: "auto", mt: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5, px: 1 }}
      >
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, color: "text.disabled", letterSpacing: 1.2 }}
        >
          Links Preview
        </Typography>
        <Tooltip title="Verified Pioneer">
          <RocketLaunchIcon
            sx={{ fontSize: 18, color: "#ff715b", opacity: 0.8 }}
          />
        </Tooltip>
      </Stack>

      <PremiumCard>
        <CardContent sx={{ pt: 0, pb: 4 }}>
          {list.length === 0 ? (
            <EmptyContent
              description={`Looks like you have no ${
                type === "customLinks"
                  ? "custom links"
                  : type === "socialLinks"
                  ? "social links"
                  : "contacts"
              }  yet.`}
              img="/assets/illustrations/illustration_empty_content.svg"
            />
          ) : type === "socialLinks" ? (
            list.map((link, Id) => (
              <LinkCard
                sx={{
                  padding: "0.8rem 1.5rem",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
                key={link.Id}
                onClick={() => handleLinkClick(link)}
              >
                {/* Left side: Icon and Title */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    minWidth: 0, // Allow text to truncate if needed
                  }}
                >
                  <Box
                    color={link.Color}
                    fontSize="18px"
                    sx={{
                      marginRight: "12px",
                      flexShrink: 0, // Prevent icon from shrinking
                    }}
                  >
                    {link.Icon}
                  </Box>

                  <Typography
                    sx={{
                      color: "rgba(20, 43, 58, 1)",
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {link.Title}
                  </Typography>
                </Box>

                {/* Right side: Action buttons */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0, // Prevent buttons from shrinking
                  }}
                >
                  <a
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(link);
                    }}
                  >
                    <AutoFixHighIcon
                      sx={{
                        marginRight: "10px",
                        cursor: "pointer",
                        "&:hover": {
                          color: "#ff715b",
                          fontSize: "27px",
                        },
                      }}
                    />
                  </a>
                  <a
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(link.Id);
                    }}
                  >
                    <DeleteIcon
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          color: "#ff715b",
                          fontSize: "27px",
                        },
                      }}
                    />
                  </a>
                </Box>
              </LinkCard>
            ))
          ) : (
            list.map((link, Id) => (
              <LinkCard
                sx={{
                  padding: "0.8rem 1.5rem",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0, // Allow flex items to shrink
                  overflow: "hidden",
                }}
                key={Id}
                onClick={() => handleLinkClick(link)}
              >
                {/* Left side: Icon and Title */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    minWidth: 0, // Allow text to truncate if needed
                  }}
                >
                  {type === "contactLinks" ? (
                    <Box
                      sx={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: link.Color,
                        marginRight: "12px",
                        flexShrink: 0,
                        boxShadow: "0 0.4rem 0.8rem rgba(20, 43, 58, 0.25)",
                      }}
                    >
                      <i
                        className={link.Icon}
                        style={{
                          color: "white",
                          fontSize: "14px",
                        }}
                      />
                    </Box>
                  ) : (
                    <Avatar
                      style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "60%",
                        boxShadow: "0 0.4rem 0.8rem rgba(20, 43, 58, 0.25)",
                        marginRight: "12px",
                        flexShrink: 0, // Prevent avatar from shrinking
                      }}
                      src={link.Icon}
                      alt="User Profile Photo"
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: "rgba(20, 43, 58, 1)",
                        fontWeight: 600,
                        fontSize: "15px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {link.Title}
                    </Typography>
                    {type === "contactLinks" &&
                      link.ContactInfo &&
                      link.Name && (
                        <Typography
                          sx={{
                            color: "rgba(20, 43, 58, 0.7)",
                            fontSize: "13px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {link.ContactInfo}
                        </Typography>
                      )}
                  </Box>
                </Box>

                {/* Right side: Action buttons */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0, // Prevent buttons from shrinking
                  }}
                >
                  <a
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(link);
                    }}
                  >
                    <AutoFixHighIcon
                      sx={{
                        marginRight: "10px",
                        cursor: "pointer",
                        "&:hover": {
                          color: "#ff715b",
                          fontSize: "27px",
                        },
                      }}
                    />
                  </a>
                  <a
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(link.Id);
                    }}
                  >
                    <DeleteIcon
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          color: "#ff715b",
                          fontSize: "27px",
                        },
                      }}
                    />
                  </a>
                </Box>
              </LinkCard>
            ))
          )}
        </CardContent>
        <Box
          sx={{
            height: 6,
            width: "100%",
            background:
              type === "customLinks"
                ? "linear-gradient(90deg, #ff715b, #e65d47)"
                : type === "socialLinks"
                ? "linear-gradient(90deg, #667eea, #764ba2)"
                : type === "contactLinks"
                ? "linear-gradient(90deg, #25D366, #128C7E)"
                : "#ff715b",
          }}
        />
      </PremiumCard>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          mt: 2,
          color: "text.disabled",
          fontStyle: "italic",
        }}
      >
        Changes are reflected in real-time
      </Typography>
    </Box>
  );
}
